import { t } from "i18next";
import { z } from "zod";

export const requiredNumberMax = (maxDigits: number, maxDecimals: number, label: string) =>
  z
    .union([z.string(), z.number()])
    .optional()
    .superRefine((val, ctx) => {
      if (val === undefined || val === null || `${val}`.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", { label }),
        });
        return;
      }

      const numericValue = Number(val);

      if (numericValue === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", { label }),
        });
        return;
      }

      const normalizedValue = `${val}`;
      const [intPart, decPart] = normalizedValue.replace(/^-/, "").split(".");

      if (intPart.length > maxDigits) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.max-length-digits", { label, maxDigits }),
        });
      }
      if (decPart && decPart.length > maxDecimals) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.max-length-decimals", { label, maxDecimals }),
        });
      }
    })
    .pipe(z.coerce.number().optional());

export const optionalNumberMax = (maxDigits: number, maxDecimals: number, label: string) =>
  z
    .string()
    .optional()
    .superRefine((val, ctx) => {
      if (!val) return;
      if (Number.isNaN(Number(val))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.invalid-number", { label }),
        });
        return;
      }

      const [intPart, decPart] = val.replace(/^-/, "").split(".");

      if (intPart.length > maxDigits) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.max-length-digits", { label, maxDigits }),
        });
      }
      if (decPart && decPart.length > maxDecimals) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.max-length-decimals", { label, maxDecimals }),
        });
      }
    })
    .pipe(z.coerce.number().optional());

export const requiredStringMax = (max: number, label: string) =>
  z
    .string()
    .min(1, t("form-validation.mandatory", { label }))
    .max(max, t("form-validation.max-length", { label, max }));

export const optionalStringMax = (max: number, label: string) =>
  z.string().max(max, t("form-validation.max-length", { label, max }));

export const requiredDate = () => {
  const baseSchema = z.union([z.date(), z.string(), z.null(), z.undefined()]);

  return baseSchema.transform((value) => {
    if (value instanceof Date || value == null) {
      return value;
    }

    const parsedDate = new Date(value);

    return Number.isNaN(parsedDate.getTime()) ? value : parsedDate;
  });
};

export const optionalDate = () => {
  const baseSchema = z.union([z.date(), z.string(), z.null(), z.undefined()]);

  return baseSchema.transform((value) => {
    if (value instanceof Date || value == null) {
      return value;
    }

    const parsedDate = new Date(value);

    return Number.isNaN(parsedDate.getTime()) ? value : parsedDate;
  });
};

export const requiredBoolean = (label: string) =>
  z
    .union([z.boolean(), z.literal("")])
    .optional()
    .superRefine((val, ctx) => {
      if (val === "" || val === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", { label }),
        });
      }
    });

export const optionalBoolean = () =>
  z.union([z.boolean(), z.literal(""), z.literal("na")]).optional();

export const requiredTrue = (message: string) =>
  z.boolean().refine((val) => val === true, {
    message,
  });
